import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { doc, runTransaction, updateDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import { UserContext } from "../contexts/userContext";
import { useContext, useEffect, useState } from "react";
import { convertCurrencyFormat } from "../helpers";
import { IOrder, ISale } from "../interfaces";
import {
  BUSINESS_COLLECTION,
  colorStatus,
  SALES_COLLECTION,
  SHIFT_COLLECTION,
  statusLabel,
} from "../utils/constants";

const Order = ({
  order,
  setEditableSale,
  minimized = false,
}: {
  order: IOrder;
  setEditableSale?: (sale: ISale) => void;
  minimized?: boolean;
}) => {
  const [selectedOrder, setSelectedOrder] = useState({} as IOrder);
  const { businessData, shiftInfo, updateShift, userData } =
    useContext(UserContext);
  const canManage = !!userData && userData.role !== "KITCHEN";

  const updateTotalOrders = async () => {
    if (businessData.uid && shiftInfo?.id) {
      const shiftDocRef = doc(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        SHIFT_COLLECTION,
        shiftInfo.id
      );

      try {
        await runTransaction(db, async (transaction) => {
          const shiftDoc = await transaction.get(shiftDocRef);

          if (!shiftDoc.exists()) {
            throw "El documento del turno no existe";
          }

          const { totalOrders, totalAmount } = shiftDoc.data();
          const newTotalOrders = totalOrders > 0 ? totalOrders + 1 : 1;
          const newTotalAmount =
            totalAmount > 0 ? totalAmount + order.total : order.total;
          transaction.update(shiftDocRef, {
            totalOrders: newTotalOrders,
            totalAmount: newTotalAmount,
          });
          updateShift("updateReport", {
            totalOrders: newTotalOrders,
            totalAmount: newTotalAmount,
          });
        });
      } catch (e) {
        console.error("La transacción falló: ", e);
      }
    }
  };

  const updateStatus = async (cancelOrder?: boolean) => {
    const newState: { [key: string]: string } = {
      pending: "preparing",
      // preparing: "ready",
      preparing: "completed",
      // ready: "completed",
    };

    const orderDocRef = doc(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      SALES_COLLECTION,
      order.id
    );
    await updateDoc(orderDocRef, {
      status: cancelOrder ? "cancelled" : newState[order.status],
      updatedAt: new Date(),
    });
    if (order.status === "ready") updateTotalOrders();
  };

  const handleDeliverProduct = (index: number, type?: string) => {
    // optimistic UI: toggle locally then persist to Firestore
    if (type === "promotion") {
      const prevPromos = selectedOrder.promotion || [];
      const promotionCopy = prevPromos.map((p: any, i: number) =>
        i === index ? { ...p, delivered: !p.delivered } : p
      );
      setSelectedOrder((prev) => ({ ...prev, promotion: promotionCopy }));

      // persist
      const orderDocRef = doc(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        SALES_COLLECTION,
        order.id
      );
      updateDoc(orderDocRef, { promotion: promotionCopy }).catch((err) => {
        console.error("Error updating promotion delivered state:", err);
        // rollback
        setSelectedOrder((prev) => ({ ...prev, promotion: prevPromos }));
      });
    } else {
      const prevProducts = selectedOrder.products || [];
      const productsCopy = prevProducts.map((p: any, i: number) =>
        i === index ? { ...p, delivered: !p.delivered } : p
      );
      setSelectedOrder((prev) => ({ ...prev, products: productsCopy }));

      // persist
      const orderDocRef = doc(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        SALES_COLLECTION,
        order.id
      );
      updateDoc(orderDocRef, { products: productsCopy }).catch((err) => {
        console.error("Error updating product delivered state:", err);
        // rollback
        setSelectedOrder((prev) => ({ ...prev, products: prevProducts }));
      });
    }
  };

  useEffect(() => {
    if (order) {
      const orderCopy = {
        ...order,
        products: order?.products?.map((p) => ({
          ...p,
          delivered: p.delivered ?? false,
        })),
        promotion: order?.promotion?.map((p) => ({
          ...p,
          delivered: p.delivered ?? false,
        })),
      };
      setSelectedOrder(orderCopy);
    }
  }, [order]);

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          m={1}
        >
          <Typography variant="h4">#{selectedOrder.turn}</Typography>
          <Chip
            label={statusLabel[selectedOrder.status]}
            color={colorStatus[selectedOrder.status]}
            variant="outlined"
          />
        </Box>
        {!minimized && (
          <>
            {selectedOrder.products && selectedOrder.products.length > 0 && (
              <>
                <Typography mt={2}>Productos:</Typography>
                {selectedOrder &&
                  selectedOrder.products.map(
                    ({ name, quantityToSale, delivered }, index) => (
                      <Box
                        key={index}
                        justifyContent="space-between"
                        alignItems="center"
                        display="flex"
                        mx={1}
                        mt={1}
                        mb={2}
                      >
                        <Typography
                          sx={{
                            textDecoration: delivered ? "line-through" : "none",
                          }}
                        >
                          {name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            sx={{
                              textDecoration: delivered
                                ? "line-through"
                                : "none",
                            }}
                          >
                            {quantityToSale}
                          </Typography>
                          {selectedOrder.status !== "cancelled" && (
                            <Checkbox
                              checked={delivered}
                              onChange={() => handleDeliverProduct(index)}
                              color="primary"
                            />
                          )}
                        </Box>
                      </Box>
                    )
                  )}
              </>
            )}

            {selectedOrder.promotion && selectedOrder.promotion.length > 0 && (
              <>
                <Typography mt={2}>Promociones:</Typography>
                {selectedOrder.promotion.map(
                  ({ promotion, quantity, delivered }, index) => (
                    <Box
                      key={index}
                      justifyContent="space-between"
                      alignItems="center"
                      display="flex"
                      mx={1}
                      mt={1}
                      mb={2}
                    >
                      <Typography
                        sx={{
                          textDecoration: delivered ? "line-through" : "none",
                        }}
                      >
                        {promotion.name}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          sx={{
                            textDecoration: delivered ? "line-through" : "none",
                          }}
                        >
                          {quantity}
                        </Typography>
                        {selectedOrder.status !== "cancelled" && (
                          <Checkbox
                            checked={delivered}
                            onChange={() =>
                              handleDeliverProduct(index, "promotion")
                            }
                            color="primary"
                          />
                        )}
                      </Box>
                    </Box>
                  )
                )}
              </>
            )}
            <Typography mt={2} fontSize={14}>
              Total: {convertCurrencyFormat(selectedOrder.total)}
            </Typography>
            {selectedOrder.observation && (
              <Typography mt={2} fontSize={12}>
                Observación: {selectedOrder.observation}
              </Typography>
            )}
          </>
        )}
        <Box
          display="flex"
          mt={3}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontSize={12}>
            Actualizado:
            <br /> {selectedOrder.updatedAt}
          </Typography>
          {canManage && (
            <>
              {selectedOrder.status !== "cancelled" && (
                <>
                  {selectedOrder.status !== "completed" && (
                    <Tooltip title="Cancelar pedido">
                      <span>
                        <IconButton
                          onClick={() => updateStatus(true)}
                          size="small"
                          color="error"
                        >
                          <span className="material-symbols-rounded">
                            close
                          </span>
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {selectedOrder.status === "pending" && (
                    <Button
                      variant="outlined"
                      sx={{ borderRadius: 10 }}
                      size="small"
                      color="primary"
                      onClick={() => updateStatus()}
                    >
                      Recibir
                    </Button>
                  )}
                  {selectedOrder.status === "preparing" && (
                    <>
                      <Tooltip title="Entregar pedido">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => updateStatus()}
                            color="success"
                          >
                            <span className="material-symbols-rounded">
                              check
                            </span>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Editar venta">
                        <span>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => {
                              if (setEditableSale) {
                                setEditableSale(selectedOrder);
                              }
                            }}
                          >
                            <span className="material-symbols-rounded">
                              edit_square
                            </span>
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                  {selectedOrder.status === "ready" && (
                    <Button
                      variant="outlined"
                      onClick={() => updateStatus()}
                      size="small"
                      color="success"
                    >
                      Completar
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Order;
